import { defineStore } from 'pinia'
import { useMyTravelStore } from './myTravelStore'
import type { SelectedSeat, CarriageSeat } from '../types/seatTypes'
import type { Journey } from '../types/journeyTypes'
import type {
  Segment,
  RequiredProduct,
  BookingTariffSegment,
} from '../types/bookingTypes'
import {
  updateSeatSelection,
  rebookItems,
  cancelItems,
} from '../services/journeyService'
import { submitProducts } from '../utils/submitProducts'
import { useBookingStore } from './bookingStore'
import type { PassengerInfo } from '../types/passengerTypes'

interface SeatProduct {
  tariffCode: string
  price: {
    amount: number
    currency: string
  }
  description: string
}

interface SeatReservationState {
  selectedSeats: { [key: string]: SelectedSeat[] }
  currentJourney: Journey | null
  passengerInfo: {
    outbound: PassengerInfo[]
    inbound: PassengerInfo[]
  }
  totalSeats: number
  seatPriceMap: {
    [direction: string]: {
      [propertyCode: string]: SeatProduct
    }
  }
  standardSeatProduct: {
    outbound: SeatProduct | null
    inbound: SeatProduct | null
  }
  seatProperties: { [key: string]: string }
  seatProducts: { [key: string]: any }
  seatPropertyDescriptions: Record<string, string>
  nextPassengerToMove: {
    outbound: number
    inbound: number
  }
  selectedSeatPrices: Record<string, number>
  rotationPhase: {
    outbound: boolean
    inbound: boolean
  }
  existingSeatProducts: Record<string, any[]>
  assignedPassengersByDirection: {
    outbound: Set<string>
    inbound: Set<string>
  }
}

export const useSeatReservationStore = defineStore('seatReservation', {
  state: (): SeatReservationState => ({
    selectedSeats: {} as { [key: string]: SelectedSeat[] },
    currentJourney: null as Journey | null,
    passengerInfo: {
      outbound: [] as PassengerInfo[],
      inbound: [] as PassengerInfo[],
    },
    totalSeats: 0,
    seatPriceMap: {} as Record<string, Record<string, SeatProduct>>,
    standardSeatProduct: {
      outbound: null as SeatProduct | null,
      inbound: null as SeatProduct | null,
    },
    seatProperties: {} as { [key: string]: string },
    seatProducts: {} as { [key: string]: any },
    seatPropertyDescriptions: {} as Record<string, string>,
    nextPassengerToMove: {
      outbound: 0,
      inbound: 0,
    },
    selectedSeatPrices: {} as Record<string, number>,
    rotationPhase: {
      outbound: false,
      inbound: false,
    },
    existingSeatProducts: {} as Record<string, any[]>,
    assignedPassengersByDirection: {
      outbound: new Set<string>(),
      inbound: new Set<string>(),
    },
  }),

  getters: {
    hasTransfers: (state) => {
      if (!state.currentJourney) return false
      return state.currentJourney.outbound_booking_tariff_segments.length > 1
    },

    hasInboundTrip: (state) => {
      if (!state.currentJourney) return false
      return (
        state.currentJourney.inbound_segments &&
        state.currentJourney.inbound_segments.length > 0
      )
    },

    hasSeatReservation: (state) => (direction: string, segmentId: string) => {
      const segments = (
        direction === 'outbound'
          ? state.currentJourney?.outbound_booking_tariff_segments
          : state.currentJourney?.inbound_booking_tariff_segments
      ) as Segment[] | undefined

      const segment = segments?.find((s) => s.id === segmentId)
      return (
        segment?.required_products?.some(
          (product: RequiredProduct) =>
            product.seat_properties?.length > 0 || false
        ) || false
      )
    },

    hasSelectedSeats: (state) =>
      Object.values(state.selectedSeats).some((seats) => seats.length > 0),

    getSelectedSeatsForLeg:
      (state) => (direction: string, legIndex: number) => {
        const key = `${direction}_${legIndex}`
        const seats = state.selectedSeats[key] || []

        // Ensure all seats have the required properties for CarriageSeat
        return seats.map((seat) => {
          // Make sure all required CarriageSeat properties exist
          return {
            ...seat,
            id: seat.id || seat.seat_id || seat.seat_number || '',
            property_code: seat.property_code || '',
            seat_number: seat.seat_number || '',
            sequence_number: seat.sequence_number || 0,
            x_pos: seat.x_pos || 0,
            y_pos: seat.y_pos || 0,
            status: seat.status || 'AVAILABLE',
            carriage: seat.carriage || '1',
          }
        })
      },

    getSelectedSeatsCount: (state) => (direction: string, legIndex: number) => {
      const key = `${direction}_${legIndex}`
      return state.selectedSeats[key]?.length || 0
    },

    getTotalPassengerCount() {
      const bookingStore = useBookingStore()
      return bookingStore.passengers?.length || 0
    },

    totalSelectedSeatsPrice: (state) => {
      return Object.values(state.selectedSeatPrices).reduce(
        (total, price) => total + (price || 0),
        0
      )
    },

    hasExistingSeatProducts: (state): boolean => {
      return Object.keys(state.existingSeatProducts).length > 0
    },
  },

  actions: {
    initializeJourney(bookingData: any) {
      this.rotationPhase = {
        outbound: false,
        inbound: false,
      }
      this.nextPassengerToMove = {
        outbound: 0,
        inbound: 0,
      }
      this.assignedPassengersByDirection = {
        outbound: new Set<string>(),
        inbound: new Set<string>(),
      }

      const myTravelStore = useMyTravelStore()

      const existingOutboundSeats = myTravelStore.outboundSelectedSeats
      const existingInboundSeats = myTravelStore.inboundSelectedSeats
      const existingOutboundPrice = myTravelStore.outboundTotalSeatPrice
      const existingInboundPrice = myTravelStore.inboundTotalSeatPrice

      this.clearSeats()

      this.existingSeatProducts = {
        outbound: [],
        inbound: [],
      }

      const outboundSegments =
        bookingData.outbound_booking_tariff_segments ||
        bookingData.booking?.outbound_booking_tariff_segments ||
        []
      const inboundSegments =
        bookingData.inbound_booking_tariff_segments ||
        bookingData.booking?.inbound_booking_tariff_segments ||
        []

      outboundSegments.forEach((segment: Segment) => {
        segment.additional_products?.forEach((product: any) => {
          if (product.type === 'SF') {
            this.existingSeatProducts.outbound.push({
              ...product,
              direction: 'outbound',
            })
          }
        })

        segment.required_products?.forEach((product: any) => {
          if (product.type === 'SF') {
            this.existingSeatProducts.outbound.push({
              ...product,
              direction: 'outbound',
            })
          }
        })
      })

      inboundSegments.forEach((segment: Segment) => {
        segment.additional_products?.forEach((product: any) => {
          if (product.type === 'SF') {
            this.existingSeatProducts.inbound.push({
              ...product,
              direction: 'inbound',
            })
          }
        })

        segment.required_products?.forEach((product: any) => {
          if (product.type === 'SF') {
            this.existingSeatProducts.inbound.push({
              ...product,
              direction: 'inbound',
            })
          }
        })
      })

      const outboundSegmentsForDisplay = outboundSegments.map(
        (segment: Segment) => {
          const hasSeatReservation = segment.required_products?.some(
            (product: any) => 'seat' in product
          )
          return {
            departure_station: segment.departure_station,
            arrival_station: segment.arrival_station,
            travel_date: segment.travel_date,
            departure_time: segment.departure_time,
            departure_timestamp:
              segment.departure_timestamp ||
              `${segment.travel_date}T${segment.departure_time}`,
            service_name: segment.service_name,
            service_type: segment.service_type,
            service_identifier: segment.service_identifier,
            validity_service: segment.validity_service,
            validity_start_date: segment.validity_start_date,
            has_seat_reservation: hasSeatReservation,
          }
        }
      )

      const inboundSegmentsForDisplay =
        inboundSegments.map((segment: Segment) => {
          const hasSeatReservation = segment.required_products?.some(
            (product: any) => 'seat' in product
          )
          return {
            departure_station: segment.departure_station,
            arrival_station: segment.arrival_station,
            travel_date: segment.travel_date,
            departure_time: segment.departure_time,
            departure_timestamp:
              segment.departure_timestamp ||
              `${segment.travel_date}T${segment.departure_time}`,
            service_name: segment.service_name,
            service_type: segment.service_type,
            service_identifier: segment.service_identifier,
            validity_service: segment.validity_service,
            validity_start_date: segment.validity_start_date,
            has_seat_reservation: hasSeatReservation,
          }
        }) || []

      this.currentJourney = {
        ...bookingData,
        outbound_segments: outboundSegmentsForDisplay,
        inbound_segments: inboundSegmentsForDisplay,
        outbound_booking_tariff_segments: outboundSegments,
        inbound_booking_tariff_segments: inboundSegments,
        passengers:
          bookingData.booking?.passengers || bookingData.passengers || [],
        booking: {
          ...bookingData.booking,
          passengers:
            bookingData.booking?.passengers || bookingData.passengers || [],
        },
      }

      const allOutboundSeatProducts = outboundSegments.flatMap(
        (segment: Segment, index: number) =>
          (segment.required_products || [])
            .filter(
              (product: any) =>
                'seat' in product && product.provisional && !product.cancelled
            )
            .map((product: any) => ({ ...product, segmentIndex: index }))
      )
      const uniqueOutboundProvisionalPassengerIds = Array.from(
        new Set(
          allOutboundSeatProducts
            .filter((p: any) => p.provisional && !p.cancelled && 'seat' in p)
            .map((p: any) => p.passenger_id)
        )
      )
      this.totalSeats =
        uniqueOutboundProvisionalPassengerIds.length > 0
          ? uniqueOutboundProvisionalPassengerIds.length
          : bookingData?.booking?.passengers?.length ||
            bookingData?.passengers?.length ||
            0

      const passengers =
        bookingData?.booking?.passengers || bookingData?.passengers || []

      const basePassengerInfo = passengers.map((passenger: any) => ({
        id: passenger.id,
        type: passenger.type,
        inventoryClass: passenger.inventory_class || 'STANDARD',
        segmentIndex: 0,
        itemId: null,
        seat: null,
      }))

      const outboundSeatProducts = outboundSegments.flatMap(
        (segment: Segment, index: number) =>
          (segment.required_products || [])
            .filter(
              (product: any) =>
                'seat' in product && product.provisional && !product.cancelled
            )
            .map((product: any) => ({ ...product, segmentIndex: index }))
      )
      const outboundPassengerMap = new Map<string, any>()
      outboundSeatProducts.forEach((product: any) => {
        outboundPassengerMap.set(product.passenger_id, product)
      })
      const outboundPassengers = Array.from(outboundPassengerMap.values()).map(
        (product: any) => {
          const baseInfo = basePassengerInfo.find(
            (p: PassengerInfo) => p.id === product.passenger_id
          )
          return {
            ...baseInfo,
            itemId: product.uuid,
            segmentIndex: product.segmentIndex,
            seat: product.seat,
          }
        }
      )
      if (outboundPassengers.length === 0) {
        outboundPassengers.push(...basePassengerInfo)
      }

      const inboundPassengers = inboundSegments.flatMap(
        (segment: any, index: number) => {
          const seatProducts = (segment.required_products || []).filter(
            (product: any) => 'seat' in product
          )
          if (seatProducts.length > 0) {
            let activeProducts = seatProducts.filter(
              (product: any) => product.provisional
            )

            if (activeProducts.length === 0) {
              activeProducts = seatProducts.filter(
                (product: any) => !product.cancelled
              )
            }

            if (activeProducts.length === 0) {
              activeProducts = seatProducts
            }

            return activeProducts.map((product: any) => {
              const baseInfo = basePassengerInfo.find(
                (p: PassengerInfo) => p.id === product.passenger_id
              )
              return {
                ...baseInfo,
                itemId: product.uuid,
                segmentIndex: index,
                seat: product.seat,
              }
            })
          }

          // If no seat products, return base passenger info for this segment
          return basePassengerInfo.map((passenger: PassengerInfo) => ({
            ...passenger,
            segmentIndex: index,
          }))
        }
      )

      // Set passenger info in store
      this.passengerInfo = {
        outbound:
          outboundPassengers.length > 0
            ? outboundPassengers
            : basePassengerInfo,
        inbound:
          inboundPassengers.length > 0 ? inboundPassengers : basePassengerInfo,
      }

      this.nextPassengerToMove = {
        outbound: 0,
        inbound: 0,
      }

      // After initialization, restore the saved seat selections from myTravelStore
      if (existingOutboundSeats.length > 0) {
        this.selectedSeats['outbound_0'] = existingOutboundSeats
        this.selectedSeatPrices['outbound_0'] =
          existingOutboundPrice?.amount || 0
      }
      if (existingInboundSeats.length > 0) {
        this.selectedSeats['inbound_0'] = existingInboundSeats
        this.selectedSeatPrices['inbound_0'] = existingInboundPrice?.amount || 0
      }
    },

    async initializeSeatProducts(
      productsResponse: any,
      direction: 'outbound' | 'inbound'
    ) {
      if (!productsResponse?.data?.segment?.products) {
        return
      }

      const products = productsResponse.data.segment.products
      const directionKey = `${direction}_SEATS`

      if (!this.seatPriceMap) {
        this.seatPriceMap = {}
      }
      this.seatPriceMap[directionKey] = {}

      const standardProduct = products.find(
        (p: any) =>
          p.product_type === 'SF' &&
          p.product_code === 'STANDARD_SEAT' &&
          p.passenger_fares?.[0]?.price
      )

      if (standardProduct?.passenger_fares?.[0]?.price) {
        const price = standardProduct.passenger_fares[0].price
        const tariffCode = standardProduct.passenger_fares[0].tariff_code

        this.seatPriceMap[directionKey]['STANDARD_SEAT'] = {
          tariffCode,
          price: {
            amount: price.amount / 100, // Convert from øre to kroner
            currency: price.currency,
          },
          description: this.getSeatPropertyDescription('STANDARD_SEAT'),
        }

        this.standardSeatProduct[direction] =
          this.seatPriceMap[directionKey]['STANDARD_SEAT']
      }

      products.forEach((product: any) => {
        if (
          product.product_type === 'SF' &&
          product.passenger_fares?.[0]?.price &&
          product.product_code !== 'STANDARD_SEAT'
        ) {
          const price = product.passenger_fares[0].price
          const tariffCode = product.passenger_fares[0].tariff_code

          this.seatPriceMap[directionKey][product.product_code] = {
            tariffCode,
            price: {
              amount: price.amount / 100, // Convert from øre to kroner
              currency: price.currency,
            },
            description: this.getSeatPropertyDescription(product.product_code),
          }
        }
      })
    },

    getSeatPropertyDescription(propertyCode: string): string {
      const descriptions: { [key: string]: string } = {
        PANORAMA_SEAT: 'Panoramasete',
        OFFICE_SEAT: 'Kontorsete',
        TABLE_SEAT: 'Sete ved bord',
        WINDOW_SEAT: 'Vindussete',
        POWER_SEAT: 'Sete med strømuttak',
        FAMILY_SEAT: 'Familiesete',
        FRONT_SEAT: 'Fremre sete',
        COMFORT_SEAT: 'Komfortsete',
        STANDARD_SEAT: 'Standard sete',
        AISLE_SEAT: 'Midtgangsete',
        LEGROOM_SEAT: 'Sete med god benplass',
        CHILD_SEAT: 'Barnesete',
      }
      return descriptions[propertyCode] || propertyCode
    },

    setSeatProperties(properties: any[]) {
      const propertyMap: { [key: string]: string } = {}
      properties.forEach((prop) => {
        propertyMap[prop.code] = prop.description || ''
      })
      this.seatProperties = propertyMap
    },

    clearSeats() {
      const myTravelStore = useMyTravelStore()

      const outboundSeats = this.getSelectedSeatsForLeg('outbound', 0)
      const inboundSeats = this.getSelectedSeatsForLeg('inbound', 0)

      myTravelStore.updateSelectedSeats('outbound', outboundSeats, {
        amount: this.selectedSeatPrices['outbound_0'],
        currency: 'NOK',
        inOre: true,
      })
      myTravelStore.updateSelectedSeats('inbound', inboundSeats, {
        amount: this.selectedSeatPrices['inbound_0'],
        currency: 'NOK',
        inOre: true,
      })

      this.selectedSeats = {}
      this.selectedSeatPrices = {}
      this.nextPassengerToMove = {
        outbound: 0,
        inbound: 0,
      }
      this.rotationPhase = {
        outbound: false,
        inbound: false,
      }
      this.assignedPassengersByDirection = {
        outbound: new Set<string>(),
        inbound: new Set<string>(),
      }
    },

    /**
     * Finds the correct tariff code for a passenger from the booking object
     * @param passengerId The ID of the passenger
     * @param productCode Product code (SF) to get matching tariff code
     * @returns The correct tariff code to use or undefined if not found
     */
    getCorrectTariffCode(
      passengerId: string,
      productCode: string
    ): string | undefined {
      if (productCode === 'SF') {
        for (const direction of ['outbound', 'inbound']) {
          for (const key of Object.keys(this.selectedSeats)) {
            if (key.startsWith(direction)) {
              const seats = this.selectedSeats[key]
              const passengerSeat = seats.find(
                (seat) => seat.passenger_id === passengerId
              )
              if (passengerSeat?.tariffCode) {
                return passengerSeat.tariffCode
              }
            }
          }
        }
      }

      const localBookingStore = useBookingStore()
      if (localBookingStore.booking) {
        if (
          localBookingStore.booking.outbound_booking_tariff_segments?.length
        ) {
          const segment =
            localBookingStore.booking.outbound_booking_tariff_segments[0]
          const product = segment.required_products?.find(
            (p) =>
              p.passenger_id === passengerId && p.type === 'ST' && !p.cancelled
          )

          if (product?.tariff_code) {
            return product.tariff_code
          }
        }

        if (localBookingStore.booking.inbound_booking_tariff_segments?.length) {
          const segment =
            localBookingStore.booking.inbound_booking_tariff_segments[0]
          const product = segment.required_products?.find(
            (p) =>
              p.passenger_id === passengerId && p.type === 'ST' && !p.cancelled
          )

          if (product?.tariff_code) {
            return product.tariff_code
          }
        }
      }
      if (productCode === 'SF') {
        for (const direction of ['outbound_SEATS', 'inbound_SEATS']) {
          if (this.seatPriceMap[direction]) {
            for (const [code, product] of Object.entries(
              this.seatPriceMap[direction]
            )) {
              if (product.tariffCode) {
                return product.tariffCode
              }
            }
          }
        }
        return undefined
      }
      return productCode
    },

    /**
     * Gets the tariff code for a passenger's selected seat
     * This is the preferred method since it uses the actual seat selection
     * @param passengerId The ID of the passenger
     * @param direction The journey direction (outbound/inbound)
     * @returns The correct tariff code for the selected seat, or undefined if not found
     */
    getTariffCodeFromSelectedSeat(
      passengerId: string,
      direction: 'outbound' | 'inbound'
    ): string | undefined {
      const allSelectedSeats = this.getSelectedSeatsForBooking()
      const passengerSeat = allSelectedSeats.find(
        (seat) =>
          seat.passenger_id === passengerId && seat.direction === direction
      )

      if (passengerSeat?.tariffCode) {
        return passengerSeat.tariffCode
      }
      return undefined
    },

    selectSeat(seatData: SelectedSeat) {
      const key = this.getSelectedSeatsKey(
        seatData.direction,
        seatData.legIndex
      )

      if (!this.selectedSeats[key]) {
        this.selectedSeats[key] = []
      }
      const store = this
      const directionKey =
        seatData.direction as keyof typeof store.rotationPhase
      const rotationPhaseValue = store.rotationPhase[directionKey]
      const nextPassengerMoveValue = store.nextPassengerToMove[directionKey]

      const passengerInfos =
        seatData.direction === 'outbound'
          ? this.passengerInfo.outbound
          : this.passengerInfo.inbound

      const totalPassengers = passengerInfos.length
      if (totalPassengers === 0) {
        return
      }

      if (!this.assignedPassengersByDirection[directionKey]) {
        this.assignedPassengersByDirection[directionKey] = new Set<string>()
      }

      const property_codes = Array.isArray(seatData.property_codes)
        ? seatData.property_codes
        : (seatData.property_codes && [...seatData.property_codes]) || []

      const propertyCode = property_codes[0] || 'STANDARD_SEAT'

      const seatProduct = this.getSeatProduct(propertyCode, seatData.direction)
      if (!seatProduct?.tariffCode) {
        return
      }

      // Initialize assigned passengers if needed
      if (!this.assignedPassengersByDirection[directionKey]) {
        this.assignedPassengersByDirection[directionKey] = new Set<string>()
      }

      // Check if clicked seat is already taken
      const existingSeat = store.selectedSeats[key].find(
        (s) => s.seat_number === seatData.seat_number
      )

      if (existingSeat) {
        // If seat is taken by any passenger, unselect it
        store.unselectSeat(existingSeat)
        return
      }

      let currentPassenger
      if (
        store.assignedPassengersByDirection[directionKey].size >=
          totalPassengers ||
        rotationPhaseValue
      ) {
        // Rotation phase - use the next passenger in sequence
        store.rotationPhase[
          seatData.direction as keyof typeof store.rotationPhase
        ] = true
        currentPassenger =
          passengerInfos[
            store.nextPassengerToMove[
              seatData.direction as keyof typeof store.nextPassengerToMove
            ]
          ]
      } else {
        // Initial phase - use the next unassigned passenger
        currentPassenger =
          passengerInfos[store.assignedPassengersByDirection[directionKey].size]
      }

      // If current passenger had a seat, unselect it first
      const previousSeat = store.selectedSeats[key].find(
        (s) => s.passenger_id === currentPassenger?.id
      )
      if (previousSeat) {
        store.unselectSeat(previousSeat)
        store.assignedPassengersByDirection[directionKey].delete(
          currentPassenger.id
        )
      }

      // Defensive check: Ensure currentPassenger and its id are valid
      if (!currentPassenger || !currentPassenger.id) {
        return
      }

      const newSeat = {
        ...seatData,
        passenger_id: currentPassenger.id,
        passengerId: currentPassenger.id,
        tariffCode: seatProduct.tariffCode,
      }

      store.selectedSeats[key].push(newSeat)
      store.assignedPassengersByDirection[directionKey].add(currentPassenger.id)



      if (store.rotationPhase[directionKey]) {
        store.nextPassengerToMove[directionKey] =
          (store.nextPassengerToMove[directionKey] + 1) % totalPassengers
      }
    },

    unselectSeat(seat: SelectedSeat) {
      const key = this.getSelectedSeatsKey(seat.direction, seat.legIndex)
      const index = this.selectedSeats[key].findIndex(
        (s) => s.seat_number === seat.seat_number
      )
      if (index !== -1) {
        const passengerInfos =
          seat.direction === 'outbound'
            ? this.passengerInfo.outbound
            : this.passengerInfo.inbound

        this.selectedSeats[key].splice(index, 1)

        const totalSeatsSelected = Object.values(this.selectedSeats).reduce(
          (total, seats) => total + seats.length,
          0
        )

        const directionKey = seat.direction as keyof typeof this.rotationPhase
        const nextPassengerKey =
          seat.direction as keyof typeof this.nextPassengerToMove

        if (totalSeatsSelected === 0) {
          this.rotationPhase[directionKey] = false
          this.nextPassengerToMove[nextPassengerKey] = 0
        } else if (this.rotationPhase[directionKey]) {
          // During rotation phase, always move to the next passenger when a seat is unselected
          this.nextPassengerToMove[nextPassengerKey] =
            (this.nextPassengerToMove[nextPassengerKey] + 1) %
            passengerInfos.length
        }

        // Delete price if it exists
        delete this.selectedSeatPrices[
          `${seat.passenger_id}-${seat.property_code}`
        ]

        // Remove passenger from assigned passengers
        const assignedPassengersKey =
          seat.direction as keyof typeof this.assignedPassengersByDirection
        this.assignedPassengersByDirection[assignedPassengersKey].delete(
          seat.passenger_id
        )
      }
    },

    async handleSeatSelection({
      seat,
      segment,
      direction,
      legIndex,
    }: {
      seat: CarriageSeat
      segment: Segment
      direction: 'outbound' | 'inbound'
      legIndex: number
    }) {
      const myTravelStore = useMyTravelStore()

      // Get passenger infos for this direction
      const passengerInfos = this.passengerInfo[direction]
      const totalPassengers = passengerInfos.length

      // Initialize assigned passengers if needed
      if (!this.assignedPassengersByDirection[direction]) {
        this.assignedPassengersByDirection[direction] = new Set<string>()
      }
      const assignedPassengers = this.assignedPassengersByDirection[direction]

      // Get the current passenger's info
      let currentPassenger
      if (assignedPassengers.size < totalPassengers) {
        // Initial assignment phase
        currentPassenger = passengerInfos[assignedPassengers.size]
      } else {
        // Rotation phase - this direction is in rotation
        this.rotationPhase[direction as keyof typeof this.rotationPhase] = true
        currentPassenger = passengerInfos[this.nextPassengerToMove[direction]]
      }

      if (!currentPassenger) {
        console.error(
          '[DEBUG] handleSeatSelection - No current passenger found:',
          {
            assignedPassengers: Array.from(assignedPassengers),
            totalPassengers,
            nextPassengerToMove: this.nextPassengerToMove[direction],
            passengerInfos,
          }
        )
        return
      }

      // Get seat product and price
      const propertyCode = seat.property_codes?.[0] || 'STANDARD_SEAT'
      const seatProduct = this.getSeatProduct(propertyCode, direction)

      // Create seat data with all required SelectedSeat properties
      const seatData: SelectedSeat = {
        ...seat,
        passenger_id: currentPassenger.id,
        passengerId: currentPassenger.id,
        direction,
        legIndex,
        segmentId: segment.id,
        price: seatProduct?.price,
        tariffCode: seatProduct?.tariffCode || '',
        property_code: propertyCode,
        seat_id: seat.id || '',
        strategy: 'manual',
        seat_preferences: [],
        itemId: '',
        status: 'pending',
        serviceIdentifier: segment.service_identifier || '',
        departureStation: segment.departure_station._u_i_c_station_code || '',
        arrivalStation: segment.arrival_station._u_i_c_station_code || '',
        carriage: '1',
      }

      this.selectSeat(seatData)

      if (this.rotationPhase[direction as keyof typeof this.rotationPhase]) {
        this.nextPassengerToMove[
          direction as keyof typeof this.nextPassengerToMove
        ] =
          (this.nextPassengerToMove[
            direction as keyof typeof this.nextPassengerToMove
          ] +
            1) %
          totalPassengers
      }

      const updatedSeats = this.getSelectedSeatsForLeg(direction, legIndex)
      const newTotalPrice = updatedSeats.reduce(
        (total, s) => total + (s.price?.amount || 0),
        0
      )
      myTravelStore.updateSelectedSeats(direction, updatedSeats, {
        amount: newTotalPrice,
        currency: 'NOK',
        inOre: true,
      })

      // NOTE: We don't call patchBookingWithProduct here because it would create duplicate products
      // The DirectJourney.vue and SeatReservationPage.vue components already handle adding seat products
      // This prevents the issue of duplicate seat products being added to the booking
    },

    formatSeatsForApi(seats: SelectedSeat[]): {
      seats: Array<{ carriage: string; number: string; item_ref: number }>
    } {
      return {
        seats: seats
          .filter((seat) => {
            if (!seat.item_ref) {
              return false
            }
            if (this.currentJourney) {
              const journeySegments =
                seat.direction === 'outbound'
                  ? this.currentJourney.outbound_booking_tariff_segments
                  : this.currentJourney.inbound_booking_tariff_segments

              const segments = (journeySegments as Segment[]) || []
              const segment = segments.find(
                (s: Segment) => s.id === seat.segmentId
              )
              if (!segment) {
                return false
              }

              // IMPORTANT: ONLY look for ST products in required_products
              // We need to find the ticket product (ST) for this passenger, NOT a product with the same item_ref
              const ticketProduct = segment.required_products?.find(
                (p: RequiredProduct) =>
                  p.passenger_id === seat.passenger_id &&
                  p.type === 'ST' && // Only ST type products can receive seat assignments
                  !p.cancelled
              )

              if (!ticketProduct) {
                return false
              }
              seat.item_ref = ticketProduct.item_ref?.toString()
              return true
            }

            if (!this.currentJourney) {
              return false
            }
            return true
          })
          .map((seat) => {
            return {
              carriage: seat.carriage || '1',
              number: seat.seat_number,
              item_ref: Number(seat.item_ref),
            }
          }),
      }
    },

    findSeatReservationSegment(direction) {
      try {
        const bookingStore = useBookingStore()
        const segments =
          direction === 'outbound'
            ? bookingStore.bookingData?.outbound_booking_tariff_segments ||
              this.currentJourney?.outbound_booking_tariff_segments
            : bookingStore.bookingData?.inbound_booking_tariff_segments ||
              this.currentJourney?.inbound_booking_tariff_segments

        if (!segments?.length) {
          return null
        }

        // First check if any segment has a required product with seat properties or seat assignment
        // This indicates seat reservation is supported on that segment
        for (const segment of segments) {
          const hasSeatInRequiredProduct = segment.required_products?.some(
            (p) => p.seat_properties?.length > 0 || p.seat
          )

          if (hasSeatInRequiredProduct) {
            return segment
          }
        }

        // // Fallback: Check if any segment is from an express bus service (typically supports seats)
        // for (const segment of segments) {
        //   const isExpressBus =
        //     segment.booking_journey_segments?.[0]?.service_type?.modality ===
        //     'EXPRESS_BUS_SERVICE'
        //   if (isExpressBus) {
        //     console.log(
        //       `[DEBUG] Found express bus segment which likely supports seat reservation: ${segment.id}`
        //     )
        //     return segment
        //   }
        // }

        // // If no segment specifically indicates seat support, return the first segment as fallback
        // console.log(
        //   '[DEBUG] No segment with explicit seat support found, using first segment as fallback'
        // )
        return segments[0]
      } catch (error) {
    
        return null
      }
    },

    // Helper function to find ticket product (ST) for a passenger
    findTicketProduct(
      bookingNumber: string,
      passengerId: string,
      direction: 'outbound' | 'inbound',
      segmentId?: string // Optional specific segment ID to search in
    ) {
      console.log('[FIND_TICKET_DEBUG] ===== FINDING TICKET PRODUCT =====', {
        bookingNumber,
        passengerId,
        direction,
        segmentId
      })
      
      try {
        // Get booking store
        const bookingStore = useBookingStore()

        // Try to find in booking data first
        if (bookingStore.bookingData) {
          console.log('[FIND_TICKET_DEBUG] Found booking data, searching segments')

          const segments =
            direction === 'outbound'
              ? bookingStore.bookingData.outbound_booking_tariff_segments
              : bookingStore.bookingData.inbound_booking_tariff_segments

          console.log('[FIND_TICKET_DEBUG] Segments found:', {
            direction,
            segmentCount: segments?.length || 0,
            segmentIds: segments?.map(s => s.id) || []
          })

          if (segments?.length) {
            // If segmentId is provided, search only in that specific segment
            if (segmentId) {
              console.log('[FIND_TICKET_DEBUG] Searching specific segment:', segmentId)
              const targetSegment = segments.find(segment => segment.id === segmentId)
              
              if (targetSegment) {
                console.log('[FIND_TICKET_DEBUG] Target segment found:', {
                  segmentId: targetSegment.id,
                  requiredProductsCount: targetSegment.required_products?.length || 0,
                  requiredProducts: targetSegment.required_products?.map(p => ({
                    type: p.type,
                    passenger_id: p.passenger_id,
                    cancelled: p.cancelled,
                    item_ref: p.item_ref
                  })) || []
                })
                
                const product = targetSegment.required_products?.find(
                  (p: any) =>
                    p.type === 'ST' &&
                    p.passenger_id === passengerId &&
                    !p.cancelled
                )
                
                if (product) {
                  console.log('[FIND_TICKET_DEBUG] Found matching ticket product:', {
                    type: product.type,
                    passenger_id: product.passenger_id,
                    cancelled: product.cancelled,
                    item_ref: product.item_ref
                  })
                  return product
                } else {
                  console.log('[FIND_TICKET_DEBUG] No matching ticket product found in target segment')
                }
              } else {
                console.log('[FIND_TICKET_DEBUG] Target segment not found:', segmentId)
              }
            } else {
              console.log('[FIND_TICKET_DEBUG] Searching all segments for ticket product')
              // If no segmentId provided, search all segments (original behavior)
              for (const segment of segments) {
                console.log('[FIND_TICKET_DEBUG] Checking segment:', {
                  segmentId: segment.id,
                  requiredProductsCount: segment.required_products?.length || 0
                })
                
                const product = segment.required_products?.find(
                  (p: any) =>
                    p.type === 'ST' &&
                    p.passenger_id === passengerId &&
                    !p.cancelled
                )
                
                if (product) {
                  console.log('[FIND_TICKET_DEBUG] Found ticket product in segment:', segment.id)
                  return product
                }
              }
            }
          } else {
            console.log('[FIND_TICKET_DEBUG] No segments found in booking data')
          }
        } else {
          console.log('[FIND_TICKET_DEBUG] No booking data found, trying currentJourney fallback')
        }

        // Fallback to current journey data if booking store doesn't have data
        console.log('[FIND_TICKET_DEBUG] Falling back to currentJourney data')
        const segments =
          direction === 'outbound'
            ? this.currentJourney?.outbound_booking_tariff_segments
            : this.currentJourney?.inbound_booking_tariff_segments

        if (!segments?.length) {
          console.log('[FIND_TICKET_DEBUG] No segments found in currentJourney either')
          return null
        }

        // If segmentId is provided, search only in that specific segment
        if (segmentId) {
          const targetSegment = segments.find(segment => segment.id === segmentId)
          if (targetSegment) {
            const product = targetSegment.required_products?.find(
              (p: any) =>
                p.type === 'ST' &&
                p.passenger_id === passengerId &&
                !p.cancelled
            )
            if (product) return product
          }
        } else {
          // If no segmentId provided, search all segments (original behavior)
          for (const segment of segments) {
            const product = segment.required_products?.find(
              (p: any) =>
                p.type === 'ST' && p.passenger_id === passengerId && !p.cancelled
            )
            if (product) return product
          }
        }

        console.log('[FIND_TICKET_DEBUG] No ticket product found anywhere')
        return null
      } catch (error) {
        console.error('[FIND_TICKET_DEBUG] Error in findTicketProduct:', error)
        return null
      }
    },

    // Helper function to find all seat products (SF) for a passenger in the specified direction
    // This helps cancel multiple products if they exist due to previous bugs/issues
    findAllSeatProducts(
      bookingNumber: string,
      passengerId: string,
      direction: 'outbound' | 'inbound'
    ): any[] {
      console.log('[FIND_PRODUCTS_DEBUG] ===== FINDING ALL SEAT PRODUCTS =====', {
        bookingNumber,
        passengerId,
        direction
      })
      
      const products: any[] = []
      try {
        const bookingStore = useBookingStore()

        if (bookingStore.bookingData) {

          const segmentKey =
            direction === 'outbound'
              ? 'outbound_booking_tariff_segments'
              : 'inbound_booking_tariff_segments'
          if (bookingStore.bookingData?.[segmentKey]?.length) {
            
            for (const segment of bookingStore.bookingData[segmentKey]) {
              
              if (segment.additional_products?.length) {
                const matchingProducts = segment.additional_products.filter(
                  (p: any) => p.type === 'SF' && p.passenger_id === passengerId
                )

                if (matchingProducts.length) {
                  products.push(...matchingProducts)
                }
              }
            }
          }
        }

        // Also check our store's existing products cache
        if (this.existingSeatProducts?.[direction]?.length) {
          const matchingProducts = this.existingSeatProducts[direction].filter(
            (p) => p.passenger_id === passengerId
          )

          if (matchingProducts.length) {
            // Filter out any products that are already in our list (by item_id or uuid)
            const uniqueProducts = matchingProducts.filter((newProduct) => {
              return !products.some(
                (existingProduct) =>
                  (existingProduct.item_id &&
                    newProduct.item_id &&
                    existingProduct.item_id === newProduct.item_id) ||
                  (existingProduct.uuid &&
                    newProduct.uuid &&
                    existingProduct.uuid === newProduct.uuid)
              )
            })

            if (uniqueProducts.length) {
              products.push(...uniqueProducts)
            }
          }
        }
        
        console.log('[FIND_PRODUCTS_DEBUG] Final products found:', {
          totalProductsFound: products.length,
          products: products.map(p => ({
            item_ref: p.item_ref,
            type: p.type,
            cancelled: p.cancelled,
            segmentId: p.segmentId || 'unknown'
          }))
        })
        
      } catch (error) {}

      return products
    },

    async patchBookingWithProduct(
      bookingNumber: string,
      tariffCode: string,
      direction: 'outbound' | 'inbound',
      passengerId: string,
      existingProductId?: string,
      newSeat?: { carriage: string; number: string },
      useSimplifiedPath: boolean = false, // Flag to use simplified path
      segmentId?: string // Specific segment ID to use instead of findSeatReservationSegment
    ) {
      const bookingStore = useBookingStore()
      const myTravelStore = useMyTravelStore()

      // Try unified approach if seat is provided
      if (newSeat && newSeat.carriage && newSeat.number) {
        try {
    

          // Get journey data
          if (!this.currentJourney) {
            throw new Error('No journey data available')
          }

          // 1. Determine the correct item_ref for seat assignment
          // Check if we have a pre-assigned item_ref from the calling component (TransferJourney)
          // This ensures we use the specific segment's product that supports seats
          let ticketProduct = null
          let itemRefToUse = null
          
          if (existingProductId) {
            itemRefToUse = existingProductId
            // We trust the calling component has provided the correct item_ref
          } else {
            // Fallback to finding ticket product
            ticketProduct = this.findTicketProduct(
              bookingNumber,
              passengerId,
              direction
            )
            
            if (!ticketProduct) {
              throw new Error(
                `No ticket product found for passenger ${passengerId} in ${direction} direction`
              )
            }
            
            if (!ticketProduct.item_ref) {
              throw new Error(
                `Ticket product found but missing item_ref for passenger ${passengerId}`
              )
            }
            
            itemRefToUse = ticketProduct.item_ref
          }




          // 2. Find ALL existing seat products for this passenger in this direction
          // This ensures we cancel ALL products to avoid having multiple per passenger
          // Initialize products variables before any logic to avoid reference errors
          const existingSeatProducts = this.findAllSeatProducts(
            bookingNumber,
            passengerId,
            direction
          )
          const activeProducts = existingSeatProducts
            ? existingSeatProducts.filter((p) => !p.cancelled)
            : []

          // 3. Handle seat products (cancel all existing ones and create new one)
          if (existingSeatProducts && existingSeatProducts.length > 0) {

            // Note: activeProducts is already initialized above

            try {
              // Get all the necessary data for the rebooking process
              const segments =
                direction === 'outbound'
                  ? this.currentJourney.outbound_booking_tariff_segments
                  : this.currentJourney.inbound_booking_tariff_segments

              if (!segments || segments.length === 0) {
                throw new Error(`No segments found for ${direction} direction`)
              }

              let segment
              if (segmentId) {
                // Find the specific segment by ID
                segment = segments.find(s => s.id === segmentId)
                if (!segment) {
                  throw new Error(`Segment with ID ${segmentId} not found in ${direction} segments`)
                }
              } else {
                // Fallback to finding seat reservation segment
                const segmentWithSeatSupport = this.findSeatReservationSegment(direction)
                segment = segmentWithSeatSupport || segments[0]
              }

              const bookingJourneySegment =
                segment.booking_journey_segments?.[0]

              if (!bookingJourneySegment) {
                throw new Error('Journey segment not found')
              }

              // Get passenger info
              const passenger = this.currentJourney.passengers?.find(
                (p) => p.id === passengerId
              )
              if (!passenger) {
                throw new Error(
                  `Passenger ${passengerId} not found in journey data`
                )
              }

              const scheduleDate = new Date(
                bookingJourneySegment.departure_date_time
              )
                .toISOString()
                .split('T')[0]


              const segmentFilteredProducts = segmentId 
                ? activeProducts.filter((product: any) => {
                    if (product.segmentId === segmentId) {
                      return true
                    }
                    const currentSegment = segments.find((s: any) => s.id === segmentId)
                    if (currentSegment?.additional_products) {
                      return currentSegment.additional_products.some((ap: any) => ap.item_ref === product.item_ref)
                    }
                    return false
                  })
                : activeProducts

              const itemIdsToCancel: number[] = []

              if (segmentFilteredProducts.length > 0) {

                segmentFilteredProducts.forEach((product) => {
                  if (product.item_ref && !isNaN(Number(product.item_ref))) {
                    itemIdsToCancel.push(Number(product.item_ref))
                  } else if (
                    product.item_id &&
                    !isNaN(Number(product.item_id))
                  ) {
                    itemIdsToCancel.push(Number(product.item_id))
                  }
                })

                if (itemIdsToCancel.length > 0) {
                  console.log('[CANCEL_DEBUG] ===== CANCELLING PRODUCTS =====', {
                    direction,
                    passengerId,
                    segmentId,
                    bookingNumber,
                    itemIdsToCancel,
                    segmentFilteredProducts: segmentFilteredProducts.map((p: any) => ({
                      item_ref: p.item_ref,
                      tariff_code: p.tariff_code,
                      type: p.type,
                      cancelled: p.cancelled,
                      segmentId: p.segmentId || 'unknown'
                    })),
                    reasonForCancellation: 'Existing products found - cancelling before creating new'
                  })
                  
                  const cancellationResponse = await cancelItems(
                    bookingNumber,
                    itemIdsToCancel
                  )
                  
                  if (!cancellationResponse || cancellationResponse?.error) {
                  }
                }
              }

              const submitSegment = {
                origin:
                  bookingJourneySegment.departure_station._u_i_c_station_code,
                origin_name: bookingJourneySegment.departure_station.name,
                destination:
                  bookingJourneySegment.arrival_station._u_i_c_station_code,
                destination_name: bookingJourneySegment.arrival_station.name,
                start_validity_date: scheduleDate,
                start_validity_time: new Date(
                  bookingJourneySegment.departure_date_time
                )
                  .toTimeString()
                  .split(' ')[0],
                direction: direction,
                service_name: bookingJourneySegment.service_name,
                service_identifier: segment.service_identifier || '',
                items: [
                  {
                    product_code: 'SF',
                    passenger_id: passengerId,
                    tariff_code: tariffCode,
                  },
                ],
              }

              const passengers = [
                {
                  id: passengerId,
                  type: passenger.type || 'ADT',
                },
              ]

              await submitProducts(
                bookingNumber,
                [submitSegment]
              )
              
              await new Promise(resolve => setTimeout(resolve, 100))
              console.log('[DEBUG] New seat product created during update, continuing to seat assignment')
            } catch (error) {
              console.error('[ERROR] Failed to rebook seat product:', error)
              throw error
            }
          } else if (useSimplifiedPath) {
    
            const segments =
              direction === 'outbound'
                ? this.currentJourney.outbound_booking_tariff_segments
                : this.currentJourney.inbound_booking_tariff_segments

            if (!segments || segments.length === 0) {
              throw new Error(`No segments found for ${direction} direction`)
            }

            let segment
            if (segmentId) {
              segment = segments.find(s => s.id === segmentId)
              if (!segment) {
                throw new Error(`Segment with ID ${segmentId} not found in ${direction} segments`)
              }
            } else {
              segment = segments[0]
            }
            const bookingJourneySegment = segment.booking_journey_segments?.[0]

            if (!bookingJourneySegment) {
              throw new Error('Journey segment not found')
            }

            const departureDateTime = new Date(
              bookingJourneySegment.departure_date_time
            )
            const dateStr = departureDateTime.toISOString().split('T')[0]
            const timeStr = departureDateTime.toTimeString().split(' ')[0]

            const passenger = this.currentJourney.passengers?.find(
              (p) => p.id === passengerId
            )
            if (!passenger) {
              throw new Error(
                `Passenger ${passengerId} not found in journey data`
              )
            }

            const submitSegment = {
              origin:
                bookingJourneySegment.departure_station._u_i_c_station_code,
              origin_name: bookingJourneySegment.departure_station.name,
              destination:
                bookingJourneySegment.arrival_station._u_i_c_station_code,
              destination_name: bookingJourneySegment.arrival_station.name,
              start_validity_date: dateStr,
              start_validity_time: timeStr,
              direction: direction,
              service_name: bookingJourneySegment.service_name,
              service_identifier: segment.service_identifier || '',
              items: [
                {
                  product_code: 'SF',
                  passenger_id: passengerId,
                  tariff_code: tariffCode,
                },
              ],
            }

            await submitProducts(
              bookingNumber,
              [submitSegment]
            )
            
            await new Promise(resolve => setTimeout(resolve, 100))
          }

          const activeTicketProduct = this.findTicketProduct(
            bookingNumber,
            passengerId,
            direction,
            segmentId
          )
          
          if (!activeTicketProduct || !activeTicketProduct.item_ref) {
            throw new Error(
              `No active ticket product found for passenger ${passengerId} in ${direction} direction. Cannot assign seat to cancelled product.`
            )
          }

          const finalItemRef = activeTicketProduct.item_ref

          await updateSeatSelection(bookingNumber, {
            seats: [
              {
                carriage: newSeat.carriage,
                number: newSeat.number,
                item_ref: finalItemRef,
              },
            ],
          })


          await bookingStore.fetchBookingData(bookingNumber)

          return { success: true }
        } catch (error) {

          throw error
        }
      }
    },

    findProductById(productId: string): any {
      const bookingStore = useBookingStore()
      const bookingData = bookingStore.bookingData

      if (bookingData) {
        if ((bookingData as any).additional_products?.length) {
          const product = (bookingData as any).additional_products.find(
            (p: any) => p.uuid === productId || p.item_id === productId
          )
          if (product) {
            return product
          }
        }

        if ((bookingData as any).outbound_booking_tariff_segments?.length) {
          for (const segment of (bookingData as any).outbound_booking_tariff_segments) {
            if ((segment as any).additional_products?.length) {
              const product = (segment as any).additional_products.find(
                (p: any) => p.uuid === productId || p.item_id === productId
              )
              if (product) {
                return product
              }
            }
          }
        }

        if ((bookingData as any).inbound_booking_tariff_segments?.length) {
          for (const segment of (bookingData as any).inbound_booking_tariff_segments) {
            if ((segment as any).additional_products?.length) {
              const product = (segment as any).additional_products.find(
                (p: any) => p.uuid === productId || p.item_id === productId
              )
              if (product) {
                return product
              }
            }
          }
        }
      }

      if (this.existingSeatProducts) {
        for (const direction of ['outbound', 'inbound']) {
          if (this.existingSeatProducts[direction]?.length) {
            const product = this.existingSeatProducts[direction].find(
              (p: any) => p.uuid === productId || p.item_id === productId
            )
            if (product) {
              return product
            }
          }
        }
      }
      return null
    },

    isSeatSelected(
      seatNumber: string,
      direction: string,
      legIndex: number
    ): boolean {
      const key = `${direction}_${legIndex}`
      return (
        this.selectedSeats[key]?.some(
          (seat) => seat.seat_number === seatNumber
        ) || false
      )
    },

    getSelectedSeatsForBooking(): SelectedSeat[] {
      const allSeats: SelectedSeat[] = []

      Object.entries(this.selectedSeats).forEach(([key, seats]) => {
        const [rawDirection, legIndex] = key.split('_')
        const direction =
          rawDirection === 'outbound' || rawDirection === 'inbound'
            ? rawDirection
            : 'outbound'

        (seats as SelectedSeat[]).forEach((seat: SelectedSeat) => {
          const seatWithDirection: SelectedSeat = {
            ...seat,
            direction: direction as 'outbound' | 'inbound',
            sequence_number: typeof legIndex === 'string' ? parseInt(legIndex, 10) : legIndex,
            passenger_id: seat.passenger_id,
          }
          allSeats.push(seatWithDirection)
        })
      })

      return allSeats
    },

    getPassengerById(passengerId: string, journey: Journey): any {
      const passenger = journey?.passengers?.find(
        (p: any) => p.id === passengerId
      )
      return passenger || undefined
    },

    getSelectedSeatsKey(direction: string, legIndex: number): string {
      return `${direction}_${legIndex}`
    },

    getSegmentStartTime(segment: any): string {
      const journeySegment = segment.booking_journey_segments?.[0]
      const departureDateTime = journeySegment?.departure_date_time
      return departureDateTime
        ? new Date(departureDateTime).toTimeString().slice(0, 8)
        : '00:00:00'
    },

    /**
     * Gets the numeric price for a given seat and direction
     * @param seat - The CarriageSeat object
     * @param direction - The travel direction (outbound/inbound)
     * @returns Numeric price or 0 if no price found
     */
    getSeatPrice(seat: any, direction: 'outbound' | 'inbound'): number {
      try {

        if (seat.price && typeof seat.price === 'object' && seat.price.amount !== undefined) {
          const amount = typeof seat.price.amount === 'number' ? seat.price.amount : parseFloat(seat.price.amount)
          return isNaN(amount) ? 0 : amount
        }

        const directionKey = `${direction}_SEATS`
        if (this.seatPriceMap && this.seatPriceMap[directionKey]) {

          if (seat.property_codes && Array.isArray(seat.property_codes)) {
            for (const propertyCode of seat.property_codes) {
              const seatProduct = this.seatPriceMap[directionKey][propertyCode]
              if (seatProduct && seatProduct.price && seatProduct.price.amount !== undefined) {
                const amount = typeof seatProduct.price.amount === 'number' ? seatProduct.price.amount : parseFloat(seatProduct.price.amount)
                return isNaN(amount) ? 0 : amount
              }
            }
          }

          if (seat.property_code) {
            const seatProduct = this.seatPriceMap[directionKey][seat.property_code]
            if (seatProduct && seatProduct.price && seatProduct.price.amount !== undefined) {
              const amount = typeof seatProduct.price.amount === 'number' ? seatProduct.price.amount : parseFloat(seatProduct.price.amount)
              return isNaN(amount) ? 0 : amount
            }
          }
        }
        const standardProduct = this.standardSeatProduct[direction]
        if (standardProduct && standardProduct.price && standardProduct.price.amount !== undefined) {
          const amount = typeof standardProduct.price.amount === 'number' ? standardProduct.price.amount : parseFloat(standardProduct.price.amount)
          return isNaN(amount) ? 0 : amount
        }

        return 0
      } catch (error) {

        return 0
      }
    },

    /**
     * Gets the seat product for a given property code and direction
     * @param propertyCode - The seat property code
     * @param direction - The travel direction (outbound/inbound)
     * @returns SeatProduct object or null if not found
     */
    getSeatProduct(propertyCode: string, direction: 'outbound' | 'inbound'): any {
      try {
        const directionKey = `${direction}_SEATS`
        
        if (this.seatPriceMap[directionKey] && this.seatPriceMap[directionKey][propertyCode]) {
          return this.seatPriceMap[directionKey][propertyCode]
        }
        
        if (this.standardSeatProduct[direction]) {
          return this.standardSeatProduct[direction]
        }
        
        return null
      } catch (error) {

        return null
      }
    },

    /**
     * Gets the formatted price for display for a given seat and direction
     * @param seat - The CarriageSeat object
     * @param direction - The travel direction (outbound/inbound)
     * @returns Formatted price string or empty string if no price
     */
    getSeatPriceForDisplay(seat: any, direction: 'outbound' | 'inbound'): string {
      try {
        const amount = this.getSeatPrice(seat, direction)
        if (amount <= 0) return ''

        if (seat.price && typeof seat.price === 'object' && seat.price.currency) {
          const currency = seat.price.currency !== 'undefined' ? seat.price.currency : 'NOK'
          return `${amount.toFixed(0)} ${currency}`
        }

        const directionKey = `${direction}_SEATS`
        if (this.seatPriceMap && this.seatPriceMap[directionKey]) {
          if (seat.property_codes && Array.isArray(seat.property_codes)) {
            for (const propertyCode of seat.property_codes) {
              const seatProduct = this.seatPriceMap[directionKey][propertyCode]
              if (seatProduct && seatProduct.price && seatProduct.price.currency) {
                const currency = seatProduct.price.currency !== 'undefined' ? seatProduct.price.currency : 'NOK'
                return `${amount.toFixed(0)} ${currency}`
              }
            }
          }

          if (seat.property_code) {
            const seatProduct = this.seatPriceMap[directionKey][seat.property_code]
            if (seatProduct && seatProduct.price && seatProduct.price.currency) {
              const currency = seatProduct.price.currency !== 'undefined' ? seatProduct.price.currency : 'NOK'
              return `${amount.toFixed(0)} ${currency}`
            }
          }
        }

        const standardProduct = this.standardSeatProduct[direction]
        if (standardProduct && standardProduct.price && standardProduct.price.currency) {
          const currency = standardProduct.price.currency !== 'undefined' ? standardProduct.price.currency : 'NOK'
          return `${amount.toFixed(0)} ${currency}`
        }

        return `${amount.toFixed(0)} NOK`
      } catch (error) {

        return ''
      }
    },
  },
})
